import { useEffect, useRef } from 'react';

import { OTP_LENGTH } from '../lib/otp';

type OtpInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-invalid'?: boolean;
  'aria-labelledby'?: string;
  digitAriaLabel?: (index: number, total: number) => string;
};

export function OtpInput({
  length = OTP_LENGTH,
  value,
  onChange,
  id,
  disabled,
  autoFocus,
  'aria-invalid': ariaInvalid,
  'aria-labelledby': ariaLabelledBy,
  digitAriaLabel,
}: OtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const focusIndex = (index: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(index, length - 1))];
    if (!el) return;
    el.focus();
    el.select();
  };

  useEffect(() => {
    if (!autoFocus || disabled) return;
    focusIndex(value.length < length ? value.length : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  function applyDigits(digits: string, startIndex = 0) {
    const cleaned = digits.replace(/\D/g, '').slice(0, length);
    if (!cleaned) return;

    const prefix = value.slice(0, startIndex);
    const next = (prefix + cleaned).slice(0, length);
    onChange(next);
    focusIndex(Math.min(startIndex + cleaned.length, length - 1));
  }

  function handleChange(index: number, raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length > 1) {
      applyDigits(digits, index);
      return;
    }
    if (!digits) return;

    const digit = digits[0]!;
    if (index < value.length) {
      onChange((value.slice(0, index) + digit + value.slice(index + 1)).slice(0, length));
    } else {
      onChange((value + digit).slice(0, length));
    }
    if (index < length - 1) focusIndex(index + 1);
  }

  function handleBackspace(index: number) {
    if (index < value.length) {
      onChange(value.slice(0, index) + value.slice(index + 1));
      focusIndex(index);
      return;
    }
    if (value.length > 0) {
      onChange(value.slice(0, -1));
      focusIndex(value.length - 1);
      return;
    }
    if (index > 0) focusIndex(index - 1);
  }

  return (
    <div className="otp-input-group" id={id} role="group" aria-labelledby={ariaLabelledBy}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          className="otp-input-cell"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={index === 0 ? length : 1}
          value={value[index] ?? ''}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-label={
            digitAriaLabel ? digitAriaLabel(index + 1, length) : `Digit ${index + 1} of ${length}`
          }
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              handleBackspace(index);
              return;
            }
            if (e.key === 'ArrowLeft' && index > 0) {
              e.preventDefault();
              focusIndex(index - 1);
              return;
            }
            if (e.key === 'ArrowRight' && index < length - 1) {
              e.preventDefault();
              focusIndex(index + 1);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            applyDigits(e.clipboardData.getData('text'), index);
          }}
          onFocus={(e) => e.currentTarget.select()}
        />
      ))}
    </div>
  );
}
